import { useQueryClient } from '@tanstack/react-query';

import React, { createContext, useMemo } from 'react';

import { ComponentMetadataModel, ExpressionMetadataModel } from '../../../api/api';
import { ExpressionMetadataProvider } from '../../../components/ExpressionMetadataContext';
import { ViewLoader } from '../../FlowDesigner/Components/ViewLoader';
import useFlowQueries from '../../FlowDesigner/Hooks/useFlowQueries';
import ExpressionVariableScopeCalculatorService from '../../FlowDesigner/Services/ExpressionVariableScopeCalculatorService';
import useApiUserInterfaceMetadata from '../Hooks/useApiUserInterfaceMetadata';
import { VirtualComponentMetadata } from '../Metadata/VirtualComponentMetadata';

export interface IUserInterfaceMetadataContext {
	componentByName: Map<string, ComponentMetadataModel>;
	expressionMetadataList: ExpressionMetadataModel[];
}

const emptyUserInterfaceMetadataContext: IUserInterfaceMetadataContext = {
	componentByName: new Map<string, ComponentMetadataModel>(),
	expressionMetadataList: [],
};

export const UserInterfaceMetadataContext = createContext<IUserInterfaceMetadataContext>(emptyUserInterfaceMetadataContext);

export default function UserInterfaceMetadataProvider({ children }: React.PropsWithChildren<{}>) {
	const { data: metadata, isLoading } = useApiUserInterfaceMetadata();
	const flowQueries = useFlowQueries();
	const queryClient = useQueryClient();

	const value = useMemo<IUserInterfaceMetadataContext>(() => {
		if (!metadata) return emptyUserInterfaceMetadataContext;

		const componentByName = (metadata.components || []).reduce((acc, component) => {
			acc.set(component.name, component);
			return acc;
		}, new Map<string, ComponentMetadataModel>());

		// Add virtual component metadata (components that exist in the designer but not in the runtime)
		VirtualComponentMetadata.forEach((virtualComponent) => {
			componentByName.set(virtualComponent.name, virtualComponent);
		});

		return { componentByName, expressionMetadataList: metadata.expressions || [] };
	}, [metadata]);

	const expressionScopeCalculator = useMemo(
		() => (metadata ? new ExpressionVariableScopeCalculatorService(metadata.expressions || [], flowQueries, queryClient) : null),
		[metadata, flowQueries, queryClient],
	);

	const isLoaded = !isLoading && !!metadata;

	return (
		<UserInterfaceMetadataContext.Provider value={value}>
			{!isLoaded && <ViewLoader />}
			{isLoaded && (
				<ExpressionMetadataProvider expressions={metadata?.expressions} expressionScopeCalculator={expressionScopeCalculator}>
					{children}
				</ExpressionMetadataProvider>
			)}
		</UserInterfaceMetadataContext.Provider>
	);
}
